// @ts-nocheck
import React, { useState, useEffect, useContext, useRef } from 'react';
import Fetch from '@/utils/Fetch';
import { useQuery } from '@tanstack/react-query';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Breadcrumbs from '@/components/breadcrumbs';
import SettingsMenu from '@/components/settings/menu';
import Router from 'next/router';
import * as Enums from '@/utils/enums';
import Button from '@/components/button';
import { Button as ManButton } from '@mantine/core';
import Helper from '@/utils/helper';
import ToastContext from '@/utils/toast-context';
import SCMessageBarContext from '@/utils/contexts/sc-message-bar-context';
import Storage from '@/utils/storage';
import ConfirmAction from '@/components/modals/confirm-action';
import Tabs from '@/components/tabs';
import Table from '@/components/table';
import KendoPager from '@/components/kendo/kendo-pager';
import CellStatus from '@/components/cells/status-old';
import CellDate from '@/components/cells/date';
import CellBold from '@/components/cells/bold';
import BusyIndicatorContext from '@/utils/busy-indicator-context';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCComboBox from '@/components/sc-controls/form-controls/sc-combobox';
import SCSwitch from '@/components/sc-controls/form-controls/sc-switch';
import constants from '@/utils/constants';
import SCDropdownList from '@/components/sc-controls/form-controls/sc-dropdownlist';
import { Box, Flex, Button as MantineButton, Text, Title, Tooltip, Loader, Divider } from '@mantine/core';
import { IconRefresh, IconInfoCircle } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router'
import {canShowErrorLive, visibleLiveErrors, onIntegrationChange} from '@/services/integration-service';
import usePopupClosed from '@/hooks/usePopupClosed';


const bugLogging = false; // used when debugging something hairy => bugLogging && console.log(``);

const COLUMNS = [{
  Label: 'Item',
  ColumnName: 'ItemDisplay',
  CellType: 'none',
}, {
  Label: 'Message',
  ColumnName: 'Message',
  CellType: 'none',
}, {
  Label: 'Module',
  ColumnName: 'IntegrationModule',
  CellType: 'status',
}, {
  Label: 'Sync Status',
  ColumnName: 'SyncStatus',
  CellType: 'status',
}, {
  Label: 'External Doc Number',
  ColumnName: 'ExternalDocNumber',
  CellType: 'none',
}, {
  Label: 'Updated Date',
  ColumnName: 'ModifiedDate',
  CellType: 'date',
}];

function IntegrationManage() {

  const busyIndicator = useContext(BusyIndicatorContext);
  const watchPopup = usePopupClosed();
  const toast = useContext(ToastContext);
  const { setIntegrationErrorActive } = useContext<any>(SCMessageBarContext);

  let currPath = '';
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const [integrationPartners, setIntegrationPartners] = useState<any[]>([]);
  const [selectedIntegrationPartner, setSelectedIntegrationPartner] = useState<any>(null);
  const [selectedIntegrationProcess, setSelectedIntegrationProcess] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState("Integration Settings");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [inputErrors, setInputErrors] = useState<any>({});
  const [inputCommissioning, setInputCommissioning] = useState<any>({});
  const [selectedPartnerTenant, setSelectedPartnerTenant] = useState<any>(null);
  const [inputPartner, setInputPartner] = useState<any>({});
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
  const [isLive, setIsLive] = useState(true);
  const [lastInventorySync, setLastInventorySync] = useState<any>(null);
  const [canResync, setCanResync] = useState(false)
  const [isSubmittingCredentials, setIsSubmittingCredentials] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  }

  const updateSelectedIntegration = (int: any) => {
    setInputErrors({});
    setUsername("");
    setPassword("");
    setSelectedIntegrationProcess(null);
    setSelectedIntegrationPartner(int);
    setDelayCommissioning(false);
  };

  const getIntegrationProcess = async () => {
    if (!selectedIntegrationPartner) {
      setSelectedIntegrationProcess(null);
      return;
    }

    const integrationProcess = await Fetch.get({
      url: "/Integration/GetIntegrationProcess",
      params: {
        integrationPartner: selectedIntegrationPartner
      }
    });

    setSelectedIntegrationProcess(integrationProcess);
    const { commissioningInputs, partnerSettings } = integrationProcess;
    setInputCommissioning(commissioningInputs ?? {});
    setInputPartner(partnerSettings ?? {});
    busyIndicator.setText!!!(null);
  };

  
  useEffect(() => {
    getIntegrationProcess();
  }, [selectedIntegrationPartner]);

  useEffect(() => {
    return onIntegrationChange(() => getIntegrationProcess())
  }, [selectedIntegrationPartner]);

  const { data: integrationPartnersData } = useQuery(
    ['integrationPartners'],
    () => Fetch.get({ url: '/Integration/GetIntegrationPartners' })
  );

  useEffect(() => {
    if (integrationPartnersData) {
      setIntegrationPartners(integrationPartnersData.Results);
    }
  }, [integrationPartnersData]);

  useEffect(() => {
    getAccessStatus();

    setIsLive(window.location.origin.toLowerCase() === "https://app.servcraft.co.za");
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace, "/");
    }
  }, [accessStatus]);

  const updateIntegrationPartners = async (setBusyIfOneResult = false) => {
    const integrationPartners = await Fetch.get({
      url: "/Integration/GetIntegrationPartners"
    });
    setIntegrationPartners(integrationPartners.Results);
    if (integrationPartners.Results.length === 1 && setBusyIfOneResult) {
      busyIndicator.setText!!!("Busy...");
    }
  }

  
  const updateIntegration = async (status: any, disable: any, commissioningInputs: any, partnerSettings: any, repressSuccess = false) => {

    const result = await Fetch.post({
      url: "/Integration/SetIntegrationStatus",
      params: {
        integrationPartner: selectedIntegrationPartner,
        status,
        disable,
        commissioningInputs,
        partnerSettings
      }
    });

    if (!result.success) {
      toast.setToast!!!({
        message: result.message,
        show: true,
        type: 'error'
      });
    } else if (!repressSuccess) {
      toast.setToast!!!({
        message: `Successfully ${status === Enums.IntegrationStatus.Cancelled ? "stopped" : "saved"}`,
        show: true,
        type: 'success'
      });
    }

    await updateIntegrationPartners();
  };
    
  useEffect(() => {
    if (integrationPartners && integrationPartners.length === 1) {
      const refreshProcess = integrationPartners[0] === selectedIntegrationPartner;
      setSelectedIntegrationPartner(integrationPartners[0]);

      if (refreshProcess) {
        getIntegrationProcess();
      }
    } else {
      setSelectedIntegrationPartner(null);
    }
  }, [integrationPartners]);

  const confirmRestartCredentials = () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      confirmButtonText: "Confirm Restart",
      onConfirm: async () => {
        await submitCredentials(true);
      },
      display: true,
      text: "You will be required to re-use the same credentials as the latest prior integration.",
      heading: "Confirm Restart Integration?"
    });
  };

  const confirmRestartAuthorize = () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      confirmButtonText: "Confirm Restart",
      onConfirm: async () => {
        await authorizePartner(true);
      },
      display: true,
      text: "You will be required to re-use the same credentials as the latest prior integration.",
      heading: "Confirm Restart Integration?"
    });
  };

  const authorizePartner = async (reuseCancelled: any) => {
    if (reuseCancelled) setDelayCommissioning(true);
    setIsBusy(true);
    try {
      const authUrlResponse = await Fetch.post({
        url: "/Integration/AuthorizeWithUrl",
        params: {
          integrationPartner: selectedIntegrationPartner,
          reuseCancelled
        }
      });

      let { success, redirectUrl } = authUrlResponse;

      Helper.mixpanelTrack(constants.mixPanelEvents.createIntegration, {
        "success": success
      });

      if (success) {
        watchPopup(window.open(redirectUrl, "_blank"), () => updateIntegrationPartners(true));
      } else if (redirectUrl) {
        toast.setToast!!!({
          message: redirectUrl,
          show: true,
          type: 'error'
        });
      }
    } finally {
      setIsBusy(false);
    }
  };

  const submitCredentials = async (reuseCancelled: any) => {
    // validate username and password

    let inpErr = {};
    let isValid = true;

    if (Helper.isNullOrWhitespace(username)) {
      isValid = false;
      (inpErr as any).username = "Username is required";
    }

    // password could be a lot of spaces
    if (!password) {
      isValid = false;
      (inpErr as any).password = "Password is required";
    }

    setInputErrors(inpErr);

    if (!isValid) return;

    if (reuseCancelled) setDelayCommissioning(true);
    setIsSubmittingCredentials(true);
    let authUrlResponse;
    try {
      authUrlResponse = await Fetch.post({
        url: "/Integration/AuthorizeWithCredentials",
        params: {
          integrationPartner: selectedIntegrationPartner,
          username, password, reuseCancelled
        }
      });
    } finally {
      setIsSubmittingCredentials(false);
    }

    let { success, message } = authUrlResponse;

    Helper.mixpanelTrack(constants.mixPanelEvents.createIntegration, {
      "success": success,
      "message": message
    });

    if (!success && message) {
      if (reuseCancelled) setDelayCommissioning(false);
      toast.setToast!!!({
        message: message,
        show: true,
        type: 'error'
      });
    }

    if (success) {
      updateIntegrationPartners();
    }

  };

  const displayPartners = () => {

    return (<>
      <h3>Partners</h3>
      <div>
        {integrationPartners.map(function (int: any) {
          return (
            <div className="card-block point" onClick={() => updateSelectedIntegration(int)}>
              {int === "SageOne" ? <>
                <img src="/sageone_trans.png" alt="sage one logo" height="40"></img>
              </> : ""}
              {int === "QuickBooks" ? <>
                <img src="/C2QB_green_btn_lg_default.png" alt="quickbooks connect" height="40"></img>
              </> : ""}
              {int === "Xero" ? <>
                <img src="/xero-logo-blue.svg" alt="xero connect"></img>
              </> : ""}
            </div>
          )
        })}
      </div>
      <style jsx>{`
       .card-block {
        background-color: ${colors.white};
        border-radius: ${layout.cardRadius};
        box-shadow: ${shadows.card};
        box-sizing: border-box;
        padding: 1rem 2rem;
        position: relative;
        margin-right: 16px;
        display: inline-block;
        width: 300px;
        height: 300px;
        line-height: 300px;
        text-align: center;
    }
    .point {
      cursor: pointer;
    }
  
    .row {
      display: flex;
      justify-content: space-between;
    }
    .column {
      width: 100%;
    }

    .xero-text {
      position: absolute;
      top: -8rem;
      left: 1rem;
    }
    `}</style>
    </>)
  };

  const updateInputCommissioning = (val: any, prop: any) => {
    setInputCommissioning({
      ...inputCommissioning,
      [prop]: val
    });
  };

  const updateBoolCommissioning = (val: any, prop: any, swt: any) => {
    if (swt && swt.length == 2) {
      let newVal = val ? swt[1].Key : swt[0].Key;

      setInputCommissioning({
        ...inputCommissioning,
        [prop]: newVal
      });
    }
  };

  const mapCommissioningStringToBool = (val: any, swt: any) => {
    if (swt && swt.length == 2) {
      if (val) {
        if (val == swt[1].Key) {
          return true;
        } else {
          return false;
        }
      }
    }
    return false;
  };

  const updateInputPartner = (val: any, prop: any) => {
    setInputPartner({
      ...inputPartner,
      [prop]: val
    });
  };

  const submitPartnerTenant = async () => {
    let isValid = !!selectedPartnerTenant;
    let errors = {
      "partnerTenant": isValid ? null : "Required"
    };
    setInputErrors(errors);
    if (!isValid) return;

    setIsBusy(true);
    try {
      const result = await Fetch.post({
        url: "/Integration/SetIntegrationPartnerTenantID",
        params: {
          integrationPartner: selectedIntegrationPartner,
          partnerTenantID: selectedPartnerTenant.Key
        }
      });

      if (!result.success) {
        toast.setToast!!!({
          message: result.message,
          show: true,
          type: 'error'
        });
      } else {
        await updateIntegrationPartners();
      }
    } finally {
      setIsBusy(false);
    }
  }

  const submitCommissioningInputs = async () => {
    // validate
    let isValid = true;
    let errors = {};

    Object.keys(inputCommissioning).map(function (key: any) {
      if (!key.endsWith("List")) {
        if (Helper.isNullOrWhitespace(inputCommissioning[key])) {
          errors["inputCommissioning" + key] = "Value is required";
          isValid = false;
        }
      }
    });

    Object.keys(inputPartner).map(function (key: any) {
      if (!key.endsWith("List")) {
        let required = true;
        try {
          let list = inputPartner[key + "List"];
          if (list !== undefined && list !== null) {
            required = list.length > 0;
          }
        } catch { }

        if (required && Helper.isNullOrWhitespace(inputPartner[key])) {
          errors["inputPartner" + key] = "Value is required";
          isValid = false;
        }
      }
    });

    setInputErrors(errors);

    if (isValid) {

      let commissioningInputs = {};
      Object.keys(inputCommissioning).map(function (key: any) {
        if (!key.endsWith("List")) {
          commissioningInputs[key] = inputCommissioning[key];
        }
      });

      let partnerSettings = {};
      Object.keys(inputPartner).map(function (key: any) {
        if (!key.endsWith("List")) {
          partnerSettings[key] = inputPartner[key];
        }
      });

      setIsBusy(true);
      updateIntegration(Enums.IntegrationStatus.Live, false, commissioningInputs, partnerSettings)
        .finally(() => setIsBusy(false));
    }
  };

  const stopIntegration = async () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      confirmButtonText: "Stop Integration",
      heading: "Confirm stop integration?",
      text: "You will be able to re-authorize this integration or create a new one.",
      onConfirm: async () => {
        busyIndicator.setText!!!("Busy...");
        let commissioningInputs = {};
        Object.keys(inputCommissioning).map(function (key: any) {
          if (!key.endsWith("List")) commissioningInputs[key] = inputCommissioning[key];
        });
        let partnerSettings = {};
        Object.keys(inputPartner).map(function (key: any) {
          if (!key.endsWith("List")) partnerSettings[key] = inputPartner[key];
        });
        try {
          await updateIntegration(Enums.IntegrationStatus.Cancelled, true, commissioningInputs, partnerSettings);
        } finally {
          busyIndicator.setText!!!(null);
        }
      }
    })
  }

  const disconnectIntegration = async () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      confirmButtonText: "Disconnect",
      heading: "Disconnect integration?",
      text: "This will remove the paused integration. You can set up a new one at any time.",
      onConfirm: async () => {
        busyIndicator.setText!!!("Busy...");
        let commissioningInputs = {};
        Object.keys(inputCommissioning).map(function (key: any) {
          if (!key.endsWith("List")) commissioningInputs[key] = inputCommissioning[key];
        });
        let partnerSettings = {};
        Object.keys(inputPartner).map(function (key: any) {
          if (!key.endsWith("List")) partnerSettings[key] = inputPartner[key];
        });
        try {
          await updateIntegration(Enums.IntegrationStatus.Cancelled, true, commissioningInputs, partnerSettings);
          setIntegrationErrorActive(false);
        } finally {
          busyIndicator.setText!!!(null);
        }
      }
    });
  };

  const stopIntegrationWithCancel = async () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      confirmButtonText: "Cancel Integration",
      heading: "Confirm cancel integration setup?",
      text: "You will need to re-authorize your integration at a later stage.",
      onConfirm: async () => {

        let commissioningInputs = {};
        Object.keys(inputCommissioning).map(function (key: any) {
          if (!key.endsWith("List")) {
            commissioningInputs[key] = inputCommissioning[key];
          }
        });

        let partnerSettings = {};
        Object.keys(inputPartner).map(function (key: any) {
          if (!key.endsWith("List")) {
            partnerSettings[key] = inputPartner[key];
          }
        });
        await updateIntegration(Enums.IntegrationStatus.Cancelled, false, commissioningInputs, partnerSettings, true);
      }
    })
  }

  const [inputCommissioningFilter, setInputCommissioningFilter] = useState<any>({});

  const changeHandlerInputCommissioning = (e: any, key: any) => {
    let dummy = { ...inputCommissioningFilter };
    dummy[key] = e.target.value;
    setInputCommissioningFilter(dummy);
  };

  const getInputOptions = (list: any, key: any, selectedValue: any, filters: any) => {
    if (filters[key] === undefined) return [];
    let filter = filters[key];
    if (!list) return [];
    filter = filter !== null ? filter.toString().trim().toLowerCase() : "";
    let listTemp = [...list];
    if (filter === "") return listTemp;
    let hasMatch = listTemp.filter((x: any) => x.Value.trim().toLowerCase() === filter).map((x: any) => x.Key).includes(selectedValue);
    if (hasMatch) {
      return listTemp;
    } else {
      return listTemp.filter((x: any) => x.Value.trim().toLowerCase().indexOf(filter) > -1);
    }
  };

  const [partnerSettingsFilter, setPartnerSettingsFilter] = useState<any>({});

  const changeHandlerPartnerSettings = (e: any, key: any) => {
    let dummy = { ...partnerSettingsFilter };
    dummy[key] = e.target.value;
    setPartnerSettingsFilter(dummy);
  };


  const [refreshFilters, setRefreshFilters] = useState(0);

  useEffect(() => {

    let dummy = { ...inputCommissioningFilter };
    Object.keys(inputCommissioning).map((key: any) => {
      dummy[key] = displayInputDescription(key, inputCommissioning);
    });
    setInputCommissioningFilter(dummy);

    dummy = { ...partnerSettingsFilter };
    Object.keys(inputPartner).map((key: any) => {
      dummy[key] = displayInputDescription(key, inputPartner);
    });
    setPartnerSettingsFilter(dummy);
  }, [refreshFilters]);

  const inputCommissioningBlur = async (key: any) => {
    // have to use this method as the blur event occurs before the selected event and it messes with the selected value
    await Helper.waitABit();
    setRefreshFilters(refreshFilters + 1);
  };

  const partnerSettingsBlur = async (key: any) => {
    // have to use this method as the blur event occurs before the selected event and it messes with the selected value
    await Helper.waitABit();
    setRefreshFilters(refreshFilters + 1);
  };

  useEffect(() => {
    if (inputCommissioning) {
      let dummy = { ...inputCommissioningFilter };
      Object.keys(inputCommissioning).forEach((key: any) => {
        dummy[key] = displayInputDescription(key, inputCommissioning);
      });
      setInputCommissioningFilter(dummy);
    } else {
      setInputCommissioningFilter({});
    }
  }, [inputCommissioning]);

  useEffect(() => {
    if (inputPartner) {
      let dummy = { ...partnerSettingsFilter };
      Object.keys(inputPartner).forEach((key: any) => {
        dummy[key] = displayInputDescription(key, inputPartner);
      });
      setPartnerSettingsFilter(dummy);
    } else {
      setPartnerSettingsFilter({});
    }
  }, [inputPartner]);

  const displayInputDescription = (key: any, inputs: any) => {

    if (!inputs) {
      return "";
    }

    let val = inputs[key];
    let list = inputs[key + "List"];
    if (!list) {
      return "";
    }
    let obj = list.find((x: any) => x.Key === val);
    return obj ? obj.Value : "";
  };

  const [delayCommissioning, setDelayCommissioning] = useState(false);

  const modifyIntegrationComponent = (isLive: any, isCommissioning: any, isUnauthorized: any, commissioningInputs: any, partnerSettings: any, partnerTenantMissing: any, partnerTenants: any, partnerTenantID: any) => {

    let usePopup = isCommissioning && !delayCommissioning;

    let partnerTenantOption = Array.isArray(partnerTenants) && partnerTenants.find((x: any) => x.Key === partnerTenantID);

    const isSubmitDisabled =
      accessStatus === Enums.AccessStatus.LockedWithAccess ||
      accessStatus === Enums.AccessStatus.LockedWithOutAccess ||
      (isCommissioning && selectedIntegrationPartner === 'SageOne' && (
        !inputPartner['Company'] ||
        !(inputPartner['CompanyList'] ?? []).some((x: any) => x.Key == inputPartner['Company'])
      ));

    return (<div className={usePopup ? "overlay" : ""} onClick={(e: any) => e.stopPropagation()}>
      <div className={usePopup ? "modal-container" : ""}>

        {usePopup ? <>
          <h3>{selectedIntegrationPartner} Setup Incomplete!</h3>
          <p>You are almost there! Please complete this form and click Confirm to get your integration started.</p>
        </> : ""}

        {partnerTenantMissing && Array.isArray(partnerTenants) ? <>

          <SCDropdownList
            error={inputErrors["partnerTenants"]}
            required={true}
            label={"Select Organisation"}
            options={partnerTenants}
            value={() =>
              partnerTenants.find((x: any) => x.Key === selectedPartnerTenant?.Key)
            }
            dataItemKey="Key"
            textField="Value"
            onChange={setSelectedPartnerTenant}
            extraClasses="input-width"
          />

          <div style={{ display: "flex", justifyContent: "space-between" }} >
            <ManButton
              loading={isBusy}
              disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
              onClick={() => submitPartnerTenant()}
              style={{ backgroundColor: '#299F1D', color: '#fff', marginTop: 16 }}
            >Next</ManButton>

            <Button
              extraClasses="fit-content white-action" text={"Cancel"} onClick={stopIntegrationWithCancel} />
          </div>

        </> : <>

          {partnerTenantOption &&
            <p>Connected to <span style={{ fontWeight: 'bold' }}>{partnerTenantOption.Value}</span></p>
          }

          {!isUnauthorized && <>
            {commissioningInputs && Object.keys(commissioningInputs).map(function (key: any) {
              if (key.endsWith("List") || key.endsWith("Switch") || key.endsWith("Label") || key.endsWith("Indent")) {
                return ("");
              }
              let list = commissioningInputs[key + "List"];
              let swt = commissioningInputs[key + "Switch"];
              let label = commissioningInputs[key + "Label"];
              label = label ? label : Helper.splitWords(key);
              let indent = commissioningInputs[key + "Indent"] === true;

              if (list) {

                return (<div style={{ paddingLeft: indent ? "1rem" : "initial" }}>
                  <SCDropdownList
                    error={inputErrors["inputCommissioning" + key]} required={true} label={Helper.splitWords(key)}
                    options={getInputOptions(list, key, inputCommissioning[key], inputCommissioningFilter)}
                    value={() =>
                      list.find((x: any) => x.Key === inputCommissioning[key])
                    }
                    dataItemKey="Key"
                    textField="Value"
                    onChange={(e: any) => { updateInputCommissioning(e ? e.Key : null, key); }}
                    extraClasses="input-width"
                  />
                </div>);
              } else if (swt) {
                return (<div style={{ marginTop: "1rem", paddingLeft: indent ? "1rem" : "initial" }}>
                  <SCSwitch
                    checked={mapCommissioningStringToBool(inputCommissioning[key], swt)}
                    onChange={(e: any) => { updateBoolCommissioning(e.value, key, swt) }}
                    label={label}
                  />
                </div>);
              } else {
                return (<div style={{ paddingLeft: indent ? "1rem" : "initial" }}>
                  <SCInput
                    error={inputErrors["inputCommissioning" + key]}
                    required={true}
                    label={label}
                    value={inputCommissioning[key]}
                    onChange={(e: any) => { updateInputCommissioning(e.value, key); }} />
                </div>);
              }
            })}

            <p>Default account settings:</p>

            {partnerSettings && Object.keys(partnerSettings).map(function (key: any) {

              if (key.endsWith("List")) {
                return ("");
              }
              let list = partnerSettings[key + "List"];
              let label = Helper.splitWords(key);
              if (label === "VAT") {
                label = "Sales VAT";
              } else if (label === "VAT Zero Rated") {
                label = "Sales VAT Zero Rated";
              }

              if (list) {
                let notEmpty = list.length > 0;
                return (<>
                  <SCComboBox disabled={isLive && Enums.IntegrationPartner[selectedIntegrationPartner] === Enums.IntegrationPartner.SageOne && label === "Company"}
                    error={inputErrors["inputPartner" + key]} required={notEmpty} label={label}
                    options={getInputOptions(list, key, inputPartner[key], partnerSettingsFilter)}
                    value={() =>
                      list.find((x: any) => x.Key === inputPartner[key])
                    }
                    dataItemKey="Key"
                    textField="Value"
                    onChange={(e: any) => { updateInputPartner(e ? e.Key : null, key); }}
                    extraClasses="input-width"
                  />
                </>);
              } else {
                return (<>
                  <SCInput readOnly={isLive && Enums.IntegrationPartner[selectedIntegrationPartner] === Enums.IntegrationPartner.SageOne}
                    error={inputErrors["inputPartner" + key]} required={true} label={label} value={inputPartner[key]}
                    type="text" onChange={(e: any) => { updateInputPartner(e.value, key); }} />
                </>);
              }
            })}
          </>}

          <div style={{ display: "flex", justifyContent: "space-between" }} >
            {!isUnauthorized && (
              <ManButton
                disabled={isSubmitDisabled}
                rightSection={isBusy && <Loader variant={'oval'} size={18} color={'white'} />}
                onClick={() => submitCommissioningInputs()}
                style={{ marginTop: 16, opacity: isSubmitDisabled ? 0.5 : 1 }}
                styles={isSubmitDisabled ? { root: { cursor: 'default !important' } } : undefined}
              >{isCommissioning ? "Confirm" : "Update"}</ManButton>
            )}

            {usePopup &&
              <Button
                extraClasses="fit-content white-action" text={"Cancel"} onClick={stopIntegrationWithCancel} />
            }
          </div>
        </>}
      </div>
    </div>);
  }

    const integrationErrors = () => {
      const isSageOne = selectedIntegrationPartner === "SageOne";
      const isQuickBooks = selectedIntegrationPartner === "QuickBooks";

      const partnerIcon = isSageOne
        ? <img src="/sageone_trans.png" height="16" style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        : isQuickBooks
          ? <img src="/quickbooks.svg" height="16" style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          : <img src="/xero-logo.svg" height="16" style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />;

      const isLocked = accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess;
      const integrationErrorMessage = selectedIntegrationProcess?.errorMessage;

      const isUnauthorized = selectedIntegrationProcess?.isUnauthorized;
      const title = "Your connection is paused.";
      const subtitle = isUnauthorized
        ? "Authenticate to restore your connection."
        : "Please check the details below to restore your integration.";

      return <>
        <Title size={"h6"} c={'#E03131'}>{`${title} ${integrationErrorMessage ?? ""}`}</Title>
        <Title size={"h6"}>
          {subtitle}
        </Title>
        <br/>
        {isSageOne ? <>
          {isSubmittingCredentials
            ? <Flex h={'200px'} w={'500px'} align={'center'} justify={'center'} mt={'md'}>
                <Loader size="xl" />
              </Flex>
            : <Box maw={490}>
              <SCInput
                error={inputErrors.username}
                required={true}
                value={username}
                label="Username"
                name="username"
                onChange={(e: any) => setUsername(e.value)}
                extraClasses="input-width"
              />
              <SCInput
                error={inputErrors.password}
                required={true}
                value={password}
                type="password"
                label="Password"
                name="password"
                onChange={(e: any) => setPassword(e.value)}
                extraClasses="input-width"
              />
              <Flex gap="sm" mt="xl" align="center" justify="flex-end">
                <ManButton
                  rightSection={isSubmittingCredentials && <Loader variant={'oval'} size={18} color={'white'} />}
                  disabled={isLocked}
                  onClick={() => submitCredentials(true)}
                  style={{ backgroundColor: '#1a3faa', color: '#fff', opacity: isLocked ? 0.5 : 1 }}
                  styles={isLocked ? { root: { cursor: 'default !important' } } : undefined}
                >
                  Connect to Sage One
                </ManButton>
              </Flex>
              <Box>
                <Divider label="Or" labelPosition="center" my="lg" />
                <Text size="sm" c="dark" mb="xs">
                  Use this option if you sign into Sage using a Sage Account.
                </Text>
                <ManButton
                  variant="outline"
                  fullWidth
                  rightSection={isBusy && <Loader variant={'oval'} size={18} color={'dark'} />}
                  disabled={isLocked}
                  onClick={() => authorizePartner(true)}
                  style={{ borderColor: '#333', color: '#333', opacity: isLocked ? 0.5 : 1 }}
                  styles={isLocked ? { root: { cursor: 'default !important' } } : undefined}
                >
                  Connect with Sage Account
                </ManButton>
              </Box>
            </Box>
          }
        </> : <ManButton
          leftSection={partnerIcon}
          size='lg'
          rightSection={isBusy && <Loader variant={'oval'} size={18} color={'white'} />}
          disabled={isLocked}
          onClick={() => authorizePartner(true)}
          style={{ backgroundColor: '#299F1D', color: '#ffffff', padding: '8px 20px', marginTop: 16, opacity: isLocked ? 0.5 : 1 }}
          styles={isLocked ? { root: { cursor: 'default !important' } } : undefined}
        >
          Connect to {selectedIntegrationPartner}
        </ManButton>}
      </>
    }

    const displayIntegration = () => {

    if (!selectedIntegrationPartner) {
      return (<h3>Select an integration partner to continue</h3>);
    }

    if (!selectedIntegrationProcess) {
      return (<h3>Loading...</h3>);
    }

    const { requireAuthorize, inputCredentials, canGoLive, isLive, isCancelled, isUnauthorized,
      isCommissioning, commissioningInputs, partnerSettings, errorMessage, integrationID, statusCode,
      partnerTenantMissing, partnerTenants, partnerTenantID } = selectedIntegrationProcess;

    const serviceUnavailable = statusCode === 503 || statusCode === 404;

    let errorMessageToDisplay = errorMessage ?? "";

    if (serviceUnavailable) {
      errorMessageToDisplay += ` Service unavailable due to ${selectedIntegrationPartner} downtime, we are in contact with ${selectedIntegrationPartner} in order to resolve`;
    }

    return (<>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
          {selectedIntegrationPartner} {isCommissioning ? <>Setup Incomplete!</> : ""}
          {(isLive || isUnauthorized) && (
            <span style={{ backgroundColor: isUnauthorized ? '#FFE328' : '#47A95D', color: isUnauthorized ? '#000000' : '#fff', fontSize: '0.75rem', fontWeight: 600, padding: '2px 10px', borderRadius: '999px', lineHeight: '1.5' }}>{isUnauthorized ? 'Paused' : 'Active'}</span>
          )}
        </h2>
        {isLive && !isCommissioning && (
          <div style={{ display: 'contents' }}>
            <style>{`.stop-btn .button { background: transparent !important; border: 1px solid #E03131 !important; color: #E03131 !important; margin-top: 0; }`}</style>
            <div className="stop-btn">
              <Button
                disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                extraClasses="fit-content"
                text="Disconnect Integration"
                onClick={stopIntegration}
              />
            </div>
          </div>
        )}
        {isUnauthorized && (
          <div style={{ display: 'contents' }}>
            <style>{`.disconnect-btn .button { background: transparent !important; border: 1px solid #E03131 !important; color: #E03131 !important; margin-top: 0; }`}</style>
            <div className="disconnect-btn">
              <Button
                disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                extraClasses="fit-content"
                text="Disconnect Integration"
                onClick={disconnectIntegration}
              />
            </div>
          </div>
        )}
      </div>
      {isLive && errorMessage && (
        <Title size={"h6"} c={'#E03131'} mt="sm">{errorMessage}</Title>
      )}
      {isUnauthorized && integrationErrors()}
      <p>
        {isCommissioning && delayCommissioning ? "You are almost there! Please complete this form and click Confirm to get your integration started." : ""}
        {isLive ? <><b>Integration Settings</b></> : ""}
        {/* {isCancelled ? <>Integration is cancelled{canGoLive ? <>, but you can go live again!</> : <> and another integration set up!</>}</> : ""} */}
        {/* {isUnauthorized ? <>Integration is unauthorized, please retry if you were unsuccessful before.</> : ""} */}
        {canGoLive && !isUnauthorized && requireAuthorize ? <>
          <p>We will take you to {selectedIntegrationPartner} to be authorized, and will be redirected back here when done.</p>
          <div className="row">
            {/* {integrationID ? <> <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
              extraClasses="fit-content" text="Re-authorize Integration" onClick={confirmRestartAuthorize} /> &nbsp;</> : ""} */}

            {!(isLive || isCommissioning) ? <>
              {isBusy ? <Loader size="md" style={{ marginTop: 24 }} /> : <>
                {selectedIntegrationPartner === "QuickBooks" ? <>
                  <img src="/C2QB_green_btn_lg_default.png" alt="quickbooks connect" height="40" style={{ marginTop: 24, cursor: "pointer" }}
                    onClick={() => accessStatus !== Enums.AccessStatus.LockedWithAccess && accessStatus !== Enums.AccessStatus.LockedWithOutAccess && authorizePartner(isUnauthorized)}></img>
                </> : selectedIntegrationPartner === "Xero" ? <>
                  <img src="/xero-logo-blue.svg" alt="xero connect" height="40" style={{ marginTop: 24, cursor: "pointer" }}
                    onClick={() => accessStatus !== Enums.AccessStatus.LockedWithAccess && accessStatus !== Enums.AccessStatus.LockedWithOutAccess && authorizePartner(isUnauthorized)}></img>
                </> : selectedIntegrationPartner === "SageOne" ? <>
                  <img src="/sageone_trans.png" alt="sage one connect" height="40" style={{ marginTop: 24, cursor: "pointer" }}
                    onClick={() => accessStatus !== Enums.AccessStatus.LockedWithAccess && accessStatus !== Enums.AccessStatus.LockedWithOutAccess && authorizePartner(false)}></img>
                </> :
                  <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                    extraClasses="fit-content" text={isUnauthorized ? "Re-authorize" : "New Integration"} onClick={() => authorizePartner(isUnauthorized)} />
                }
              </>}
            </> : ""}

            &nbsp;

            <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
              extraClasses="fit-content" text="Back" onClick={() => setSelectedIntegrationPartner(null)} />

            <span className="width100"></span>
          </div>
        </> : ""}

        {canGoLive && !isUnauthorized && inputCredentials && !serviceUnavailable ? <>

          <Box maw={490}>
            <SCInput
              error={inputErrors.username}
              required={true}
              value={username}
              label="Username"
              name="username"
              onChange={(e: any) => setUsername(e.value)}
              extraClasses="input-width"
            />
            <SCInput
              error={inputErrors.password}
              required={true}
              value={password}
              type="password"
              label="Password"
              name="password"
              onChange={(e: any) => setPassword(e.value)}
              extraClasses="input-width"
            />

            {!(isLive || isCommissioning) && (
              <Flex gap="sm" mt="md" align="center" justify="flex-end">
                <ManButton
                  variant="outline"
                  maw={40}
                  disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                  extraClasses="fit-content white-action no-margin"
                  onClick={() => setSelectedIntegrationPartner(null)}
                  style={{ borderColor: '#333', color: '#333',padding: 0}}
                >Back</ManButton>
                <ManButton
                  rightSection={isSubmittingCredentials && <Loader variant={'oval'} size={18} color={'white'} />}
                  disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                  onClick={() => submitCredentials(false)}
                  style={{ backgroundColor: '#1a3faa', color: '#fff' }}
                >
                  Connect to Sage One
                </ManButton>
              </Flex>
            )}

            {selectedIntegrationPartner === "SageOne" && !(isLive || isCommissioning) && (
              <Box>
                <Divider label="Or" labelPosition="center" my="lg" />
                <Text size="sm" c="dark" mb="xs">
                  Use this option if you sign into Sage using a Sage Account.
                </Text>
                <ManButton
                  variant="outline"
                  fullWidth
                  rightSection={isBusy && <Loader variant={'oval'} size={18} color={'dark'} />}
                  disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                  onClick={() => authorizePartner(false)}
                  style={{ borderColor: '#333', color: '#333' }}
                >
                  Connect with Sage Account
                </ManButton>
              </Box>
            )}
          </Box>
        </> : ""}

        {isCommissioning || isLive || isUnauthorized ? modifyIntegrationComponent(isLive, isCommissioning, isUnauthorized, commissioningInputs, partnerSettings, partnerTenantMissing, partnerTenants, partnerTenantID) : ""}

        {!canGoLive && !isUnauthorized && !isLive && !isCommissioning ? <> You cannot setup this integration at this point in time.</> : ""}

      </p>

      <style jsx>{`
        .row {
          display: flex;
          justify-content: space-between;
        }
        .width100 {
          width: 100%;
      }
        `}</style>
    </>);
  }

  const [columnState, setColumnState] = useState(COLUMNS);
  const [integrationLines, setIntegrationLines] = useState<any>();
  const [searching, setSearching] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [searchVal, setSearchVal] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [pageSize, setPageSize] = useState(10);
  const pageSizeChanged = (size: any) => {
    setPageSize(size);
  };

  const pageChanged = (page: any) => {
    setCurrentPage(page);
  };

  const columns = React.useMemo(
    () => columnState && columnState.map(function (column: any) {
      let columnObject = {
        Header: column.Label,
        accessor: column.ColumnName,
      }

      if (column.CellType != "none") {
        switch (column.CellType) {
          case 'date':
            columnObject['Cell'] = ({ cell: { value } }: any) => <CellDate value={value} />;
            break;
          case 'status':
            columnObject['Cell'] = ({ cell: { value } }: any) => <CellStatus valueEnum={columnObject.accessor} value={value} />;
            break;
          default:
            columnObject['Cell'] = ({ cell: { value } }: any) => <CellBold value={value} />;
        }
      }

      return columnObject;
    }),
    [columnState]
  );

  useEffect(() => {
    if (currentPage == 1) {
      searchIntegrationLines();
    } else {
      setCurrentPage(1);
    }
  }, [sortField, sortDirection, pageSize]);

  useEffect(() => {
    searchIntegrationLines();
  }, [currentPage]);

  function setSort(field: any) {
    if (field != sortField) {
      setSortDirection('ascending');
    } else {
      if (sortDirection == 'ascending') {
        setSortDirection('descending');
      } else {
        setSortDirection('ascending');
      }
    }
    setSortField(field);
  }

  const searchIntegrationLines = async () => {

    setSearching(true);

    const data = await Fetch.post({
      url: `/Integration/GetIntegrationLines`,
      params: {
        pageSize: pageSize,
        pageIndex: (currentPage - 1),
        searchPhrase: searchVal,
        SortExpression: sortField,
        SortDirection: sortDirection,
      }
    });
    setIntegrationLines(data.Results);
    setTotalResults(data.TotalResults);

    setSearching(false);
  }

  const GetInitialResyncDate = async () => {
    const response = await Fetch.get({
      url: '/Integration/GetLastFullSyncDate',
      params: {
        module: Enums.Module.Inventory
      }
    });

    const date = response.Data;
    setLastInventorySync(date);
  }

  useEffect(() => {
    GetInitialResyncDate();
  }, []);

  useEffect(() => {
    updateCanResync();
  }, [lastInventorySync]);

  const updateCanResync = async () => {
    if (!lastInventorySync) {
      setCanResync(true);
      return;
    }
  
    const now = Date.now();
    const lastSyncTime = new Date(lastInventorySync).getTime();
    const timeDifference = now - lastSyncTime;
  
    const twentyFourHours = 24 * 60 * 60 * 1000;
  
    if (timeDifference >= twentyFourHours) {
      setCanResync(true);
    } else {
      setCanResync(false);
    }
  };  

  const updateIntegrationSync = async (repressSuccess = false) => {
    const result = await Fetch.post({
      url: `/Integration/ResyncModuleFromPartner?module=` + Enums.Module.Inventory,
    });

    if (!result.Success) {
      
      toast.setToast!!!({
        message: result.Message || "An error occurred.",
        show: true,
        type: 'error'
      });
    } else if (!repressSuccess) {
      toast.setToast!!!({
        message: `Successfully queued all inventory sync`,
        show: true,
        type: 'success'
      });
    }

    setLastInventorySync(result.Data)
    updateCanResync()
  };

  const resyncInventory = async () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      confirmButtonText: "Resync Inventory",
      heading: "Confirm resyncing inventory?",
      text: "This operation will queue an inventory resync.\nWarning: This can only be done every 24 hours",
      onConfirm: async () => {

        await updateIntegrationSync();
      }
    })
  }

  const getTooltipText = () => {
    if (canResync) {
      return "Click to sync data. There's a 24-hour cooldown after syncing.";
    }

    const now = new Date();
    const lastSyncDate = new Date(lastInventorySync);
    const timeDiff = now - lastSyncDate;

    const minutesSinceLastSync = timeDiff / (1000 * 60);
    const remainingTime = 1440 - minutesSinceLastSync; 

    if (remainingTime > 60) {
      const remainingHours = Math.ceil(remainingTime / 60);
      return `You can sync again in ${remainingHours} hour(s).`;
    } else {
      const remainingMinutes = Math.ceil(remainingTime);
      return `You can sync again in ${remainingMinutes} minute(s).`;
    }
  };

  const displayIntegrationLines = () => {
    return (
      <>
        <Flex mt={"2"} justify={"space-between"}>
          <div style={{ alignContent: "center"}}>
            <Text>Integration Hub</Text>
          </div>
          <div style={{ alignContent: "center"}}>
            <Tooltip label={getTooltipText()} position="left" color='grape'>
              <MantineButton disabled={!canResync} leftSection={<IconRefresh height={16}/>} onClick={resyncInventory}>Re-Sync Inventory</MantineButton>
            </Tooltip>
          </div>
        </Flex>
        <div>
          <div className={"no-items" + (integrationLines && integrationLines.length == 0 ? " no-items-visible" : "")}>
            <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
              <div className="loader"></div>
            </div>
            <img src="/job-folder.svg" alt="Folder" />
            <h3>No integration details found</h3>
            <img className="wave" src="/wave.svg" alt="wave" />
          </div>
          <div className={"table-container" + (columns && integrationLines && integrationLines.length != 0 ? " table-container-visible" : "")}>
            <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
              <div className="loader"></div>
            </div>
            {columns && integrationLines ?
              <Table
                columns={columns} data={integrationLines} setSort={setSort} sortField={sortField} sortDirection={sortDirection}
              /> : ''
            }
          </div>
          <KendoPager pageSizeChanged={pageSizeChanged} pageChanged={pageChanged} totalResults={totalResults} searchValue={searchVal} parentPageNumber={currentPage} />

        </div>
      </>
    );
  }

  return (
    <div className="settings-container">
      {/*<div className="row">
        <div className="title">
          <Breadcrumbs
            crumbs={[
              { text: 'Settings', link: '', suppressLink: true },
              { text: 'Integrations', link: '/settings/integration/manage' },
            ]}
          />
        </div>
      </div>*/}
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <div className="settings-card">

          <Tabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={[
              { text: 'Integration Settings' },
              ...(selectedIntegrationPartner ? [{ text: 'Integration Hub', suppressCount: true }] : [])
            ]} />

          {(() => {
            switch (selectedTab) {
              case "Integration Settings":
                return selectedIntegrationPartner ? displayIntegration() : displayPartners()
              case "Integration Hub":
                return selectedIntegrationPartner ? displayIntegrationLines() : null
            }
          })()}
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

      <style jsx>{`
    
    .card {
      background-color: ${colors.white};
      border-radius: ${layout.cardRadius};
      box-shadow: ${shadows.card};
      box-sizing: border-box;
      padding: 1rem 2rem;
      position: relative;
      width: 100%;
  }

  .card-grey {
    background-color: ${colors.blueGreyLight};
  }

  .card h3 {
      margin-top: 0;
  }

    .inverted {
      background-color: ${colors.blueDark};
    }

    .width100 {
        width: 100%;
    }

    .container {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      padding: 1.5rem 3rem;
      overflow-x: hidden;
    }
    .column {
      width: 100%;
    }
    .column-margin {
      margin-left: 24px;
    }
    .button-container {
      flex-shrink: 0;
      width: 9rem;
    }
    .button-container :global(.button){
      margin-top: 0.5rem;
    }
    .title {
      align-items: center;
      display: flex;
      color: ${colors.darkPrimary};
      width: 100%;
    }
    .title h1 {
      font-size: 24px;
      margin: 6px 0 0 0;
    }
    .title p {
      font-size: 14px;
      margin: 0;
    }
    .row {
      display: flex;
      justify-content: space-between;
    }
    .end {
      align-items: flex-end;
    }
    .padded {
      padding-bottom: 1rem;
    }
    .row.top-gap {
      margin-top: 2.5rem;
    }
    .heading {
      color: ${colors.blueGrey};
      font-size: 14px;
      margin-bottom: 0.5rem;
    }
    .heading-blue {
      color: ${colors.bluePrimary};
    }
    .no-jobs {
      align-items: center;
      box-sizing: border-box;
      display: none;
      flex-direction: column;
      flex-grow: 1;
      height: 100%;
      justify-content: center;
      position: relative;
      text-align: center;
    }
    .no-jobs-visible {
      display: flex;
    }
    .no-jobs h3 {
      color: ${colors.darkPrimary};
      font-size: 18px;
      line-height: 22px;
      margin: 2rem 0 0;
    }
    .no-jobs p {
      color: ${colors.blueGrey};
      margin: 0.5rem 0 0;
    }
    .no-jobs a {
      align-items: center;
      color: ${colors.bluePrimary};
      cursor: pointer;
      display: flex;
      font-weight: bold;
      margin-top: 1rem;
      text-decoration: none;
    }
    .no-jobs a img{
      margin-right: 0.5rem;
    }
    .wave {
      position: absolute;
      bottom: -1.5rem;
      left: -3rem;
      pointer-events: none;
      right: 0;
      width: calc(100% + 6rem);
    }
    .search-container :global(.search) {
      width: 528px;
    }
    .table-container {
      display: none;
      height: 100%;
      overflow-x: scroll;
      position: relative;
    }
    .table-container :global(.table) {
      margin-top: 0;
    }
    .table-container-visible {
      display: block;
    }
    .loading-overlay {
      align-items: center;
      /*background-color: rgba(28, 37, 44, 0.6);*/
      background-color: rgba(245, 248, 251, 0.6);
      bottom: 0;
      border-radius: 8px;
      display: none;
      justify-content: center;
      left: -1rem;
      position: absolute;
      right: -1rem;
      top: 0.5rem;
    }
    .loading-overlay-visible {
      display: flex;
    }
    .loading-overlay :global(.loader) {
      border-color: rgba(28, 37, 44, 0.2);
      border-left-color: ${colors.darkPrimary};
      border-width: 0.25rem;
      display: flex;
      height: 1.5rem;
      width: 1.5rem;
    }
    a {
      text-decoration: none;
    }
    .select-actions {
      display: flex;
      margin-left: -1rem;
    }
    .select-actions :global(.white-action) {
      font-size: 0.875rem;
      margin: 0 0 0 1rem; 
      width: auto;
    }
    .select-actions :global(.icon) {
      left: 1rem !important;
    }
    .download {
      margin-right: 1rem;
      width: 3rem;
    }


    .menu {
      background-color: rgba(255, 255, 255, 0.1);
        border-radius: ${layout.cardRadius};
        box-sizing: border-box;
        flex-shrink: 0;
        height: fit-content;
        margin-right: 1rem;
        margin-top: 1rem;
        padding: 0.5rem 0.5rem;
        width: 150px;
        min-height: 288px;
      }
      .menu.width300 {
          width: 300px;
      }
      .link {
        border-radius: ${layout.buttonRadius};
        box-sizing: border-box;
        color: ${colors.white};
        cursor: pointer;
        display: flex;
        padding: 0.5rem;
        text-decoration: none;
        width: 100%;
      }
      .link:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
      .current {
        background-color: rgba(255, 255, 255, 0.2);
      }
      .nonlink {
        border-radius: ${layout.buttonRadius};
        box-sizing: border-box;
        color: ${colors.white};
        display: flex;
        padding: 0.5rem;
        text-decoration: none;
        width: 100%;
        font-weight: bold;
      }

    `}</style>

    </div>
  );
}

export default IntegrationManage;

export const Route = createFileRoute('/_authenticated/settings/integration/manage')({ component: IntegrationManage })
